import { push } from 'redux-first-history';
import { all, call, put, race, select, take, takeLatest } from 'redux-saga/effects';

import embeddedService from '@app/libs/embedded/embeddedService';
import { logger } from '@app/libs/logger';
import workspaceService from '@app/libs/workspaceService';
import { InPersonHostPageLocationState } from '@app/routes/InPersonHost';
import { SigningAuthenticationPageState } from '@app/routes/Signing/Authentication';
import { loadAccount, setAuthentication } from '@app/store/account/actions';
import { ACCOUNT_LOAD_FAILURE, ACCOUNT_LOAD_SUCCESS } from '@app/store/actionTypes';
import SigningMethodType from '@app/store/contract-progress/SigningMethodType';
import Status from '@app/store/document/Status';
import Host from '@app/store/Host';
import { modalOpen } from '@app/store/modal/actions';
import {
  DocumentDetail,
  getDocumentsByIdDeprecated,
} from '@app/store/transport-layer/document/documents';
import { getEmail } from '@app/store/user/user/selectors';

import {
  fetchEmbeddedDocumentFailed,
  fetchEmbeddedDocumentRequest,
  fetchEmbeddedDocumentSucceeded,
  setError,
} from './state';

export function* fetchEmbeddedDocumentSaga({
  payload: { documentId, signable },
}: ReturnType<typeof fetchEmbeddedDocumentRequest>) {
  try {
    yield put(loadAccount());

    const { succeeded, failed } = yield race({
      succeeded: take(ACCOUNT_LOAD_SUCCESS),
      failed: take(ACCOUNT_LOAD_FAILURE),
    });

    if (!succeeded) {
      logger.error(`user load failed in while fetching embedded document`, {
        status: failed?.response?.status,
        documentId,
      });
      yield put(
        setError({
          title: '유효하지 않은 접근',
          content: '유효하지 않은 접근입니다.',
        })
      );
      return;
    }

    yield put(setAuthentication(true));

    const userEmail: ReturnType<typeof getEmail> = yield select(getEmail);
    const {
      participants,
      requester,
      signings,
      status,
      hosts,
      currentSigningOrder,
    }: DocumentDetail = yield call(getDocumentsByIdDeprecated, {
      workspaceId: workspaceService.getWorkspaceId(),
      documentId,
    });

    yield put(fetchEmbeddedDocumentSucceeded());

    const currentHost: Host | undefined = hosts.find((h: Host) => h.contactInfo === userEmail);

    const currentParticipant = participants.find(
      (p) => p.signingMethod.contactInfo === userEmail && p.signingOrder === currentSigningOrder
    );

    if (status === Status.ON_PROCESSING) {
      yield put(
        modalOpen('PARTICIPANT_PROCESSING_MODAL', {
          documentId,
          participantId: currentParticipant?.participantId,
        })
      );
    } else if (status === Status.PROCESSING_FAILED) {
      yield put(modalOpen('PARTICIPANT_PROCESSING_FAILED_MODAL'));
    } else if (status === Status.PENDING) {
      yield put(
        push('/document/aborted', {
          contactInfo: currentParticipant?.signingMethod.contactInfo,
          documentId,
          participantId: currentParticipant?.participantId,
          requester,
        })
      );
    } else if (status === Status.COMPLETED) {
      yield put(
        push('/document/completed', {
          contactInfo: currentParticipant?.signingMethod.contactInfo,
          documentId,
        })
      );
    } else if (embeddedService.isEmbedded && !signable) {
      yield put(
        push('/document/viewer', {
          contactInfo: currentParticipant?.signingMethod.contactInfo,
          documentId,
        })
      );
    } else if (userEmail !== currentParticipant?.signingMethod.contactInfo) {
      yield put(push('/document/wait-for-signing', { documentId }));
    } else if (currentParticipant && !currentParticipant.signingExpiryDate.isValid) {
      yield put(
        push('/signing/expired', { documentId, participantId: currentParticipant?.participantId })
      );
    } else if (
      !signings
        .filter((signing) => !signing.expiredAt)
        .some((s) => s.participantId === currentParticipant?.participantId)
    ) {
      yield put(
        push<SigningAuthenticationPageState>('/signing/authentication', {
          contactInfo: currentParticipant?.signingMethod.contactInfo,
          documentId,
          participantId: currentParticipant?.participantId,
          signingMethodType: currentParticipant?.signingMethod.methodType as SigningMethodType,
        })
      );
    } else if (currentHost) {
      yield put(
        push<InPersonHostPageLocationState>('/in-person/host', {
          documentId,
          currentHostDataLabel: currentHost.dataLabel,
        })
      );
    }
  } catch (error) {
    yield put(fetchEmbeddedDocumentFailed(error));
  }
}

export default function* saga() {
  yield all([takeLatest(fetchEmbeddedDocumentRequest.type, fetchEmbeddedDocumentSaga)]);
}
